import {
  Text,
  ButtonGroup,
  InlineStack,
  BlockStack,
  Button,
  Page,
  Layout,
  LegacyCard,
  RadioButton,
  Scrollable,
  ChoiceList,
  Card,
  EmptyState,
  Banner,
} from "@shopify/polaris";
import { useState, useEffect } from "react";
import Modal from "../commonComponents/modal";
import storageUtils from "../utils/storageUtils";
import { q_products, q_p_map, c_o_u_p_map, sync_p } from "../api";

export default function UpsellSetting() {
  const [productData, setProductData] = useState([]);
  const [productMap, setProductMap] = useState([]);
  const [choiceListData, setChoiceListData] = useState([]);
  const [upsellIds, setUpsellIds] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [createMapId, setCreateMapId] = useState("");
  const [selectProductMap, setSelectProductMap] = useState("");
  const user_token = storageUtils.get("user_token");
  const [isEmpty, setIsEmpty] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [disableSync, setDisableSync] = useState(false);

  useEffect(() => {
    queryShopProduct();
    queryProductMap();
  }, []);

  const addUpsell = () => {
    setModalOpen(true);
  };

  const queryShopProduct = async () => {
    const params = {
      user_token: user_token,
    };
    const response = await q_products(params);
    if (!response.data) return;
    setProductData(response.data);
    const choiceData = response.data.map((item) => {
      return {
        value: item.id.toString(),
        label: item.title,
        helpText: item.product_id + " inventory: " + item.total_inventory,
      };
    });
    setChoiceListData(choiceData);
  };

  const queryProductMap = async () => {
    const params = {
      user_token: user_token,
    };
    const response = await q_p_map(params);
    if (!response.data) return;
    setProductMap(response.data);
    if (!response.data[0]) {
      setIsEmpty(true);
      return;
    }
    setIsEmpty(false);
    handelSelectProductMap(
      response.data[0].id,
      response.data[0].upsell_ids ? response.data[0].upsell_ids : [],
    );
  };

  const handleChangeMapId = (value) => {
    setCreateMapId(value);
  };

  const createProductMap = async () => {
    setModalLoading(true);
    const params = {
      user_token: user_token,
      product_id: createMapId,
    };
    const response = await c_o_u_p_map(params);
    if (!response.data) return;
    resetData();
    queryProductMap();
  };

  const handelSelectProductMap = (id, upsellIds) => {
    setSelectProductMap(id);
    setUpsellIds(upsellIds ? upsellIds : []);
  };

  const deleteProductUpsell = async () => {
    setDeleteLoading(true);
    const params = {
      user_token: user_token,
      id: selectProductMap,
      is_delete: 1,
    };
    const response = await c_o_u_p_map(params);
    if (!response.data) return;
    resetData();
    queryProductMap();
    setDeleteLoading(false);
  };

  const saveProductUpsell = async () => {
    const params = {
      user_token: user_token,
      id: selectProductMap,
      upsell_ids: upsellIds,
    };
    setSaveLoading(true);
    const response = await c_o_u_p_map(params);
    if (!response.data) return;
    setSaveLoading(false);
  };

  const changeUpsellIds = (value) => {
    setUpsellIds(value);
  };

  const resetData = () => {
    setModalOpen(false);
    setModalLoading(false);
    setCreateMapId("");
  };

  const SyncProduct = async () => {
    const params = {
      user_token: user_token,
    };
    const response = await sync_p(params);
    if (response.status === 200) setDisableSync(true);
  };

  return (
    <>
      <Page
        fullWidth
        primaryAction={{
          content: "Add Upsell",
          onAction: () => {
            addUpsell();
          },
        }}
        secondaryActions={[
          {
            content: "Sync Product",
            onAction: () => SyncProduct(),
            disabled: disableSync,
          },
        ]}
      >
        <BlockStack gap="200">
          {disableSync ? (
            <Banner title="In the synchronized product">
              <p>
                The background process has started to synchronize the products.
                Please wait. The more products there are, the longer the
                synchronization time will be.
              </p>
            </Banner>
          ) : (
            ""
          )}

          {isEmpty ? (
            <LegacyCard sectioned>
              <EmptyState
                heading="Upsell setting is empty"
                action={{
                  content: "Add Upsell",
                  onAction: () => {
                    addUpsell();
                  },
                }}
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              ></EmptyState>
            </LegacyCard>
          ) : (
            <Layout>
              <Layout.Section variant="oneThird">
                <Card sectioned>
                  <BlockStack gap="200">
                    <Text as="h2" variant="headingSm">
                      Product Map
                    </Text>
                    <Scrollable shadow style={{ height: "700px" }} focusable>
                      {productMap.map((item) => {
                        return (
                          <Button
                            key={item.id + "map"}
                            pressed={selectProductMap === item.id}
                            onClick={() => {
                              handelSelectProductMap(item.id, item.upsell_ids);
                            }}
                            size="large"
                            variant="tertiary"
                            textAlign="left"
                            fullWidth
                          >
                            <div>{item.product_info.title}</div>
                            <div>
                              <Text as="p" tone="subdued">
                                {item.product_info.product_id}
                              </Text>
                            </div>
                          </Button>
                        );
                      })}
                    </Scrollable>
                  </BlockStack>
                </Card>
              </Layout.Section>
              <Layout.Section>
                <Card sectioned>
                  <BlockStack gap="200">
                    <Text as="h2" variant="headingSm">
                      Upsell Product
                    </Text>
                    <Scrollable shadow style={{ height: "663px" }} focusable>
                      <ChoiceList
                        allowMultiple
                        choices={choiceListData}
                        selected={upsellIds}
                        onChange={(value) => {
                          changeUpsellIds(value);
                        }}
                      />
                    </Scrollable>
                    <InlineStack align="end">
                      <ButtonGroup>
                        <Button
                          onClick={() => {
                            deleteProductUpsell();
                          }}
                          tone="critical"
                          loading={deleteLoading}
                        >
                          Delete
                        </Button>
                        <Button
                          variant="primary"
                          loading={saveLoading}
                          onClick={() => {
                            saveProductUpsell();
                          }}
                        >
                          Save
                        </Button>
                      </ButtonGroup>
                    </InlineStack>
                  </BlockStack>
                </Card>
              </Layout.Section>
            </Layout>
          )}
        </BlockStack>
      </Page>
      <Modal open={modalOpen}>
        <BlockStack gap="200">
          <Text as="h2" variant="headingSm">
            Create upsell for the product
          </Text>
          <BlockStack gap="200">
            <Scrollable shadow style={{ height: "400px" }} focusable>
              {productData.map((item) => {
                return (
                  <RadioButton
                    key={item.product_id}
                    label={item.title}
                    helpText={item.handle}
                    checked={createMapId === item.id}
                    id={item.id}
                    name="map_modal"
                    onChange={() => {
                      handleChangeMapId(item.id);
                    }}
                  />
                );
              })}
            </Scrollable>
          </BlockStack>
          <InlineStack align="end">
            <ButtonGroup>
              <Button
                variant="secondary"
                onClick={() => {
                  setModalOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  createProductMap();
                }}
                loading={modalLoading}
              >
                Create
              </Button>
            </ButtonGroup>
          </InlineStack>
        </BlockStack>
      </Modal>
    </>
  );
}
